import { defineStore } from '@webapp-next/store/define-store'; // TODO: this cant be moved to the real pinia just yet because it's getting instantiated too early in the stack
import getDpBootstrapPromise from '@/global-libs/classic-bootstrap-promise';
import {
  Country,
  BreakTypeKeyValue,
  BusinessInfo,
  LocaleInfo,
  SlotConfig,
  WebappUser,
  WebappState,
  WebappStateSetup,
} from '@webapp-next/types/store';
import { Department, Workplace } from '@/modules/people/types/people-setup';
import { getWebappClassicWindow, Webapp$dtObjConfig } from '@/types/classic';
import checkFF from '@webapp-next/lib/feature-flag';
import {
  getObjConfig,
  getObjLoginUser,
  getObjMyInstallInfo,
} from '@webapp-next/lib/session';

export function getLocaleDefaults(): LocaleInfo {
  return {
    decimal_point: '.',
    thousands_sep: '',
    int_curr_symbol: 'AUD ',
    currency_symbol: '$',
    mon_decimal_point: '.',
    locale: 'en_AU',
  };
}

const useWebappStore = defineStore('webapp', {
  state: (): WebappState => {
    return {
      user: {},
      setup: {
        SSOEnforce: false,
        SlotsConfig: [],
        InstanceId: '',
        Permissions: {},
        Workplace: [],
        WorkplaceIndexedById: {},
        Department: [],
        DepartmentIndexedById: {},
        countries: [],
        countriesIndexedById: {},
        DP_LOCALE_INFO: getLocaleDefaults(),
        CustomField: { TimeSheet: [] },
        dtBillingCreated: '',
        Portfolio: '',
        PortfolioLogoUrl: '',
        PortfolioCountryCode: '',
        OriginalHostname: '',
        DefaultShortTimeFormat: '',
        DefaultShortDateFormat: '',
        IsGenderOptionsEnabled: false,
        IsPronounsOptionsEnabled: false,
        WorkplaceConfig: {},
        Me: {} as WebappState['setup']['Me'],
      },
      isLoading: true, //  loading state for the essential data
    };
  },
  getters: {
    getBusinessInfo(state: WebappState): BusinessInfo {
      const {
        Portfolio: name,
        PortfolioLogoUrl: logo,
        PortfolioCountryCode: countryCode,
        OriginalHostname: originalHostname,
        DefaultShortTimeFormat,
        DefaultShortDateFormat,
        IsGenderOptionsEnabled,
        IsPronounsOptionsEnabled,
        DP_LOCALE_INFO: locale,
      } = state.setup;

      return {
        name,
        logo,
        countryCode,
        originalHostname,
        url: location.origin,
        timeFormat: DefaultShortTimeFormat === 'h:i A' ? '12' : '24',
        dateFormat: DefaultShortDateFormat,
        displayGender: Number(IsGenderOptionsEnabled) === 1,
        displayPronouns: Number(IsPronounsOptionsEnabled) === 1,
        localeCurrencySymbol: locale.currency_symbol || '$',
      };
    },
    getUserPermissions(state: WebappState): WebappStateSetup['Permissions'] {
      return state.setup.Permissions;
    },
    // TODO: is this required to be a computed property?
    getUserPrimaryLocationWeekStart(state: WebappState): number {
      if (!state.user.Company) {
        return 1;
      }

      return state.setup.WorkplaceConfig[state.user.Company].WEEK_START ?? 1;
    },
    getBusinessCustomTermsConfig(state: WebappState): SlotConfig[] {
      return state.setup.SlotsConfig;
    },
    daysSincePaidConversion(state: WebappState): number {
      const MILLISECONDS_IN_DAY = 24 * 3600 * 1000;
      return (
        // TODO: fix this error
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        (new Date() - new Date(state.setup.dtBillingCreated)) /
        MILLISECONDS_IN_DAY
      );
    },
    languageCode(): string {
      if (useWebappStore().getBusinessInfo.countryCode === 'AU') {
        return 'en-AU';
      }

      return 'en';
    },
    getAreasIndexedById(state: WebappState): { [key: number]: Department } {
      if (!state.setup.Department || state.setup.Department.length === 0) {
        return {};
      }

      return state.setup.Department.reduce(
        (acc: { [key: number]: Department }, area: Department) => {
          acc[area.Id] = area;
          return acc;
        },
        {}
      );
    },
  },
  actions: {
    // eslint-disable-next-line valid-jsdoc
    /**
     * @deprecated use fetchSetup instead
     */
    getSetup(): Promise<void> {
      return this.fetchSetup();
    },
    setUserBasedOnLoginState(): void {
      const config = getObjConfig();
      const loginUser = getObjLoginUser();
      if (loginUser) {
        this.setUser({
          ...config?.Me,
          Id: loginUser.EmployeeProfileObject.Id,
          UserId: loginUser.EmployeeProfileObject.UserId,
          HasEnabled2FA: loginUser.HasEnabled2FA,
          DisplayName: loginUser.EmployeeProfileObject.DisplayName,
          EmployeeLabel: config?.EmployeeLabel,
          Photo: config?.Me?.UserObjectForAPI?.Photo,
        });
        return;
      }
      this.setUser({
        ...config?.Me,
        EmployeeLabel: config?.EmployeeLabel,
        Photo: config?.Me?.UserObjectForAPI?.Photo,
      });
    },
    async fetchSetup(): Promise<void> {
      this.setLoading(true);
      const classicWindow = getWebappClassicWindow();

      // FPC-534: BFF path — consume the inline `window.__APP__.SESSION` payload
      // + the two parallel fetches (`SESSION_PROMISES.*`) that PHP kicks off
      // inline in app/forms/index.tpl.php. No extra HTTP — the bootstrap data
      // is already in flight by the time this runs.
      if (checkFF('fpc-534-session-bootstrap')) {
        const session = window.__APP__?.SESSION;
        const promises = window.__APP__?.SESSION_PROMISES;
        if (session && promises) {
          const [setupGetter, jsGetter] = await Promise.all([
            promises.desktopSetupGetter,
            promises.desktopJsGetter,
          ]);

          if (setupGetter) {
            this.setUserBasedOnLoginState();

            this.setSetup(
              {
                DP_LOCALE_INFO: session.DP_LOCALE_INFO || getLocaleDefaults(),
                ...structuredClone(session.objConfig),
                ...structuredClone(setupGetter),
                ...structuredClone(getObjMyInstallInfo() ?? {}),
              },
              true
            );

            if (jsGetter?.countries) {
              this.setCountries(jsGetter.countries);
            }
            this.setWorkplace(setupGetter.Workplace || []);
            this.setAreas(setupGetter.Department || []);
            this.setLoading(false);
            return;
          }
        }
        // BFF data missing or setup-getter failed — fall through to legacy path
      }

      // Legacy path: BootstrapPromise + window globals (UNCHANGED)
      if (checkFF('fpc-670-defer-mix-desktop-js-getter')) {
        return getDpBootstrapPromise()
          .get<Webapp$dtObjConfig['MySetup']>(
            'DP_BOOTSTRAP_JS_GET_MixDesktopSetupGetter'
          )
          .then((setup1) => {
            this.setUserBasedOnLoginState();

            this.setSetup(
              {
                DP_LOCALE_INFO:
                  classicWindow.DP_LOCALE_INFO || getLocaleDefaults(),
                ...structuredClone(getObjConfig()),
                ...structuredClone(setup1),
                ...structuredClone(getObjMyInstallInfo() ?? {}),
              },
              true
            );

            this.setWorkplace(setup1.Workplace);
            this.setAreas(setup1.Department);
            this.setLoading(false);
          })
          .catch(console.error);
      }

      return Promise.all([
        getDpBootstrapPromise().get<Webapp$dtObjConfig['MySetup']>(
          'DP_BOOTSTRAP_JS_GET_MixDesktopSetupGetter'
        ),
        getDpBootstrapPromise().get('DP_BOOTSTRAP_JS_GET_MixDesktopJSGetter'),
      ])
        .then(([setup1, setup2]) => {
          this.setUserBasedOnLoginState();

          this.setSetup(
            {
              DP_LOCALE_INFO:
                classicWindow.DP_LOCALE_INFO || getLocaleDefaults(),
              ...structuredClone(getObjConfig()),
              ...structuredClone(setup1),
              ...structuredClone(setup2),
              ...structuredClone(getObjMyInstallInfo() ?? {}),
            },
            true
          );

          this.setCountries(setup2.countries);
          this.setWorkplace(setup1.Workplace);
          this.setAreas(setup1.Department);
          this.setLoading(false);
        })
        .catch(console.error);
    },
    checkUserPermissions(permissions: string | string[]): boolean {
      try {
        if (!permissions) {
          return false;
        }

        // if setup.Permissions are Array[]
        if (this.setup.Permissions instanceof Array) {
          if (typeof permissions === 'string') {
            return !!this.setup.Permissions.includes(permissions);
          }

          for (const permission of permissions) {
            if (!this.setup.Permissions?.includes(permission)) {
              return false;
            }
          }

          return true;
          // if setup.Permissions are Object
        } else {
          if (typeof permissions === 'string') {
            return !!this.setup.Permissions[permissions];
          }

          for (const permission of permissions) {
            if (!this.setup.Permissions[permission]) {
              return false;
            }
          }

          return true;
        }
      } catch (e) {
        console.error(e);
        return false;
      }
    },
    getSetupState(): WebappState['setup'] {
      return this.setup;
    },
    getUser(): WebappState['user'] {
      return this.user;
    },
    getCountry({ id }: { id: Country['Id'] }): Country | undefined {
      if (id) {
        return this.setup.countriesIndexedById[id];
      }

      // TODO: get the default install country to return as a fallback?
      return undefined;
    },
    setCountries(countries: Country[] = []): void {
      this.setup.countries = countries;
      this.setCountriesById(
        countries.reduce<WebappStateSetup['countriesIndexedById']>(
          (reduced, country) => {
            reduced[country.Id] = country;
            return reduced;
          },
          {}
        )
      );
    },
    setCountriesById(
      countriesIndexed: WebappStateSetup['countriesIndexedById'] = {}
    ): void {
      this.setup.countriesIndexedById = countriesIndexed;
    },
    getWorkplace(id: Workplace['Id']): Workplace | undefined {
      return this.setup.WorkplaceIndexedById[id];
    },
    setWorkplace(workplace: Workplace[] = []): void {
      this.setup.Workplace = workplace;
      this.setWorkplaceById(
        workplace.reduce<WebappStateSetup['WorkplaceIndexedById']>(
          (reduced, workplace) => {
            reduced[workplace.Id] = workplace;
            return reduced;
          },
          {}
        )
      );
    },
    setWorkplaceById(
      workplaceIndexed: WebappStateSetup['WorkplaceIndexedById'] = {}
    ): void {
      this.setup.WorkplaceIndexedById = workplaceIndexed;
    },
    getAreaById(id: Department['Id']): Department | undefined {
      return this.setup.DepartmentIndexedById[id];
    },
    setAreas(area: Department[] = []): void {
      this.setup.Department = area;
      this.setAreasById(
        area.reduce<WebappStateSetup['DepartmentIndexedById']>(
          (reduced, area) => {
            reduced[area.Id] = area;
            return reduced;
          },
          {}
        )
      );
    },
    setAreasById(
      areaIndexed: WebappStateSetup['DepartmentIndexedById'] = {}
    ): void {
      this.setup.DepartmentIndexedById = areaIndexed;
    },
    setLoading(loading = true): void {
      this.isLoading = loading;
    },
    getBusinessBreakTypes(): BreakTypeKeyValue[] {
      return this.setup.SlotsConfig.map((breakType: SlotConfig) => ({
        value: breakType.SlotLabel.split('(')[0]
          .trim()
          .toUpperCase()
          .split(' ')[0],
        name: breakType.SlotName,
      }));
    },
    setSetup(setup: WebappStateSetup, override = false): void {
      setup = setup || {};

      if (override) {
        this.setup = setup;
      }

      this.setup = { ...this.setup, ...setup };
    },
    setUser(user: Partial<WebappUser> = {}): void {
      this.user = user;
    },
    getInstanceId(): WebappStateSetup['InstanceId'] {
      return this.setup.InstanceId;
    },
    getLocations(): Workplace[] {
      return this.setup.Workplace || [];
    },
    getAreas(): Department[] {
      return this.setup.Department || [];
    },
    getUserUserId(): WebappUser['UserId'] {
      return this.user?.UserId ?? 0;
    },
    getUserId(): WebappUser['Id'] {
      return this.user?.Id ?? 0; // return employee id
    },
    setLocationBusinessNumber(
      locationId: number,
      businessNumber: string
    ): void {
      const locationIndex = this.setup.Workplace.findIndex(
        (location: { Id: number }) => location.Id === locationId
      );

      if (locationIndex !== -1) {
        this.setup.Workplace[locationIndex].BusinessNumber = businessNumber;
      }
    },
  },
});

export default useWebappStore;
